<template>
  <div class="bb-client-form">
    <transition name="fade" appear>
      <loader-fly v-if="loaderRun"></loader-fly>
    </transition>
    <div class="bb-client-form-controls" @wheel="scrollHeader">
      <div class="bb-client-form-controls__title">
        {{ currentContactType === clientId ? 'Client\'s card' : 'Partner\'s card' }}
      </div>
      <div class="bb-client-form-controls-toolbar" v-if="client.id !== 'new'">
        <i class="icon-edit bb-icon bb-icon_accent" @click.stop="editForm()"/>
        <i class="icon-trash-1 bb-icon bb-icon_red" @click.stop="openDoubleDelete = true"/>
      </div>
    </div>
    <div class="bb-client-form-header" @wheel="scrollHeader">
      <div class="bb-client-form-header-avatar-wrapper">
        <img class="bb-client-form-header-avatar-wrapper__avatar"
             :src="(client.image || imgSrc) ? client.image.path || imgSrc : avatarUserDefault">
        <transition name="fade">
          <img class="bb-client-form-header-avatar-wrapper__edit"
               src="@/assets/images/icon-photo.png"
               v-if="client.id === 'new'|| editable">
        </transition>
        <input class="bb-client-form-header-avatar-wrapper__input"
               accept="image/*"
               @click.stop
               @change="processFile"
               type="file"
               v-if="client.id === 'new'|| editable">
        <transition name="fade">
          <span class="bb-client-form-header-user-info__code" v-if="!editable">
          {{ generateClientCode(client) }}
        </span>
        </transition>
      </div>
      <div class="bb-client-form-header-user-info">
        <div class="bb-client-form-header-user-info__fullname">
          <transition name="slide-fade">
            <span v-if="client.clientType === 1">
              {{ client.firstName || `${contactTypeName} name` }} {{ client.lastName }}
            </span>
          </transition>
          <transition name="slide-fade">
            <span v-if="client.clientType === 2">
              {{ client.clientCompany || 'Company name' }}
            </span>
          </transition>
          <transition name="fade">
            <i class="icon-crown-circle bb-client-form-header-avatar-wrapper__vip"
               v-if="client.id !== 'new' && !editable && client.vip"/>
          </transition>
        </div>
        <div class="bb-client-form-header-user-info-totals" v-if="client.id !== 'new'">
          <button class="bb-client-form-header-user-info__balance"
                  :class="{'bb-client-form-header-user-info__balance_negative': getAccountBalance() < 0}"
                  @click="goToSoa()">
            <i class="icon-cost"></i>
            {{ this.convertNumber(getAccountBalance()) }} AED
          </button>
          <button class="bb-client-form-header-user-info__balance"
                  :class="{
                    'bb-client-form-header-user-info__balance_negative': totalCreditNotes < 0,
                    'bb-client-form-header-user-info__balance_positive': totalCreditNotes > 0
                  }"
                  @click="getToSoaAndOpenTaxCreditNotes()">
            CN
            {{ this.convertNumber(Math.abs(totalCreditNotes)) }}
          </button>
        </div>
        <div class="bb-client-form-header-new-proforma">
          <button class="bb-client-form-header-new-proforma__button bb-button"
                  @click.stop="popUpOpen = 'proforma'"
                  v-if="client.id !== 'new'">
            NEW PROFORMA
          </button>
          <transition name="bounce">
            <div class="bb-popup bb-popup__proforma"
                 v-if="popUpOpen === 'proforma'"
                 v-click-outside="hidePopUp">
              <radio name="clientType"
                     @radioChange="setNewProforma"
                     value="1"
                     radio-text="Individual"
                     class="bb-popup__item"/>
              <radio name="clientType"
                     @radioChange="setNewProforma"
                     value="2"
                     radio-text="Entity"
                     class="bb-popup__item"/>
            </div>
          </transition>
        </div>
      </div>
    </div>
    <VuePerfectScrollbar class="bb-client-form-body" ref="scrollClientForm"
                         :class="{'bb-client-form-body_view-mode': !editable}">
      <div class="bb-client-form-body-item">
        <label class="bb-form__label bb-client-form-body-item__label">{{ contactTypeName }} Type</label>
        <div class="bb-client-form-body-item__input">
          <template v-if="editable || client.id === 'new'">
            <radio name="clientType" @radioChange="radioChange" radio-text="Individual"
                   value="1"
                   checked="checked"/>
            <radio name="clientType" @radioChange="radioChange" radio-text="Entity"
                   value="2"
                   :checked="client.clientType === 2"/>
          </template>
          <template v-else>
            <div class="bb-client-form-body-item__text">
              {{ client.clientType === clientId ? 'Individual' : 'Entity' }}
            </div>
          </template>
        </div>
      </div>
      <div class="bb-client-form-body-item">
        <label class="bb-form__label bb-client-form-body-item__label">Client's card in amoCRM</label>
        <template v-if="editable">
          <input type="text" class="bb-client-form-body-item__input bb-input bb-input_border"
                 autocomplete="off"
                 v-model="client.link">
        </template>
        <a
          v-else
          target="_blank"
          :href="client.link"
          class="bb-table-body-item-link"
          :class="{
            'bb-table-body-item-link_empty' : !client.link
          }"
          v-html="linkIcon"
        />
      </div>
      <ContactFormPartner
        v-if="currentContactType === clientId && (editable || client.partner)"
        :editable="editable"
        :client="client"
      />
      <div class="bb-client-form-body-item bb-client-form-body-item_select">
        <label class="bb-form__label bb-client-form-body-item__label">Status</label>
        <div class="bb-client-form-body-item__input">
          <template v-if="editable">
            <custom-select :list="clientStatus" @setValue="setStatus" :default-val="getStatusName(client.status)"/>
          </template>
          <template v-else>
            <div class="bb-client-form-body-item__text">
              {{ getStatusName(client.status) }}
            </div>
          </template>
        </div>
      </div>
      <div class="bb-client-form-body-item" v-if="editable || client.id === 'new'">
        <label class="bb-form__label bb-client-form-body-item__label">VIP</label>
        <div class="bb-client-form-body-item__input">
          <checkbox name="vip" @checkboxChange="checkboxChange" :value="client.vip"/>
          <span class="bb-client-form-body-item__input__text">VIP client</span>
        </div>
      </div>
      <div class="bb-client-form-body-item bb-client-form-body-item_select">
        <label class="bb-form__label bb-client-form-body-item__label">Language</label>
        <div class="bb-client-form-body-item__input">
          <template v-if="editable">
            <custom-select
              :list="clientLanguage"
              @setValue="setLanguage"
              :default-val="getLanguageName(client.languages)"/>
          </template>
          <template v-else>
            <div class="bb-client-form-body-item__text">
              {{getLanguageName(client.languages)}}
            </div>
          </template>
        </div>
      </div>
      <ContactFormPersonality
        v-if="editable || client.clientPersonalityType"
        :editable="editable"
        :client="client"
      />
      <div v-if="editable || client.riskStatus"
           class="bb-client-form-body-item bb-client-form-body-item_select"
      >
        <label class="bb-form__label bb-client-form-body-item__label">Risk</label>
        <div class="bb-client-form-body-item__input">
          <template v-if="editable">
            <custom-select
                :list="clientRisk"
                @setValue="setRisk"
                :default-val="getRiskTitle(client.riskStatus)"/>
          </template>
          <template v-else>
            <div class="bb-client-form-body-item__text">
              {{getRiskTitle(client.riskStatus)}}
            </div>
          </template>
        </div>
      </div>
      <div class="bb-client-form-body-item"
           v-if="editable || client.id === 'new' || (client.clientType === 2 && client.firstName)">
        <label class="bb-form__label bb-client-form-body-item__label">First name</label>
        <template v-if="editable">
          <input type="text" class="bb-client-form-body-item__input bb-input bb-input_border"
                 autocomplete="off"
                 v-model="client.firstName" v-autofocus>
        </template>
        <template v-else>
          <div class="bb-client-form-body-item__text">{{ client.firstName }}</div>
        </template>
      </div>
      <div class="bb-client-form-body-item"
           v-if="editable || client.id === 'new' || (client.clientType === 2 && client.lastName)">
        <label class="bb-form__label bb-client-form-body-item__label">Last name</label>
        <template v-if="editable">
          <input type="text" class="bb-client-form-body-item__input bb-input bb-input_border"
                 autocomplete="off"
                 v-model="client.lastName">
        </template>
        <template v-else>
          <div class="bb-client-form-body-item__text">{{ client.lastName }}</div>
        </template>
      </div>
      <div class="bb-client-form-body-item" ref="nickname">
        <label class="bb-form__label bb-client-form-body-item__label">Nickname</label>
        <template v-if="editable">
          <input type="text" class="bb-client-form-body-item__input bb-input bb-input_border"
                 autocomplete="off"
                 v-model="client.nickName"
                 :class="{'bb-input_error' : errors.nickName}">
          <span v-if="errors.nickName"
                class="bb-client-form-body-item_text-error">{{ errors.nickName }}</span>
        </template>
        <template v-else>
          <div class="bb-client-form-body-item__text">{{ client.nickName }}</div>
        </template>
      </div>
      <div class="bb-client-form-body-item" v-if="editable || client.id === 'new' || client.address">
        <label class="bb-form__label bb-form__label_top bb-client-form-body-item__label">Address</label>
        <template v-if="editable">
        <textarea class="bb-client-form-body-item__textarea bb-input bb-input_border"
                  v-model="client.address">
        </textarea>
        </template>
        <template v-else>
          <div class="bb-client-form-body-item__text">
            <pre>{{ client.address }}</pre>
          </div>
        </template>
      </div>
      <div class="bb-client-form-body-item"
           v-if="editable || client.id === 'new' || client.clientCompany">
        <label class="bb-form__label bb-client-form-body-item__label">Company</label>
        <template v-if="editable">
          <input type="text" class="bb-client-form-body-item__input bb-input bb-input_border"
                 autocomplete="off"
                 v-model="client.clientCompany">
        </template>
        <template v-else>
          <div class="bb-client-form-body-item__text">{{ client.clientCompany }}</div>
        </template>
      </div>
      <div class="bb-client-form-body-item" v-if="editable || client.id === 'new' || client.clientCompanyDetails">
        <label class="bb-form__label bb-form__label_top bb-client-form-body-item__label">
          Company details
        </label>
        <template v-if="editable">
        <textarea class="bb-client-form-body-item__textarea bb-input bb-input_border"
                  v-model="client.clientCompanyDetails">
        </textarea>
        </template>
        <template v-else>
          <div class="bb-client-form-body-item__text">
            <pre>{{ client.clientCompanyDetails }}</pre>
          </div>
        </template>
      </div>
      <div class="bb-client-form-body-item bb-client-form-body-item_select"
           v-if="editable || client.id === 'new' || client.businessConsultant">
        <label class="bb-form__label bb-client-form-body-item__label">Business Consultant</label>
        <div class="bb-client-form-body-item__input bb-client-form-body-item__input_capitalize">
          <template v-if="editable && users.length > 0">
            <custom-select :list="bcList"
                           @setValue="setConsultant"
                           :default-val="client.businessConsultant"/>
          </template>
          <template v-else>
            <div class="bb-client-form-body-item__text">
              {{ bcEntityById ? bcEntityById.username : '' }}
            </div>
          </template>
        </div>
      </div>
      <div class="bb-client-form-body-item bb-client-form-body-item_select"
           v-if="editable || client.id === 'new' || client.projectManager"
      >
        <label class="bb-form__label bb-client-form-body-item__label">Project Manager</label>
        <div class="bb-client-form-body-item__input bb-client-form-body-item__input_capitalize">
          <template v-if="editable && users.length > 0">
            <custom-select :list="pmList"
                           @setValue="setProjectManager"
                           :default-val="client.projectManager"/>
          </template>
          <template v-else>
            <div class="bb-client-form-body-item__text">
              {{ pmEntityById ? pmEntityById.username : '' }}
            </div>
          </template>
        </div>
      </div>
      <div class="bb-client-form-body-item"
           v-if="editable || client.id === 'new' || client.skype">
        <label class="bb-form__label bb-client-form-body-item__label">Skype</label>
        <template v-if="editable">
          <input type="text" class="bb-client-form-body-item__input bb-input bb-input_border"
                 autocomplete="off"
                 v-model="client.skype">
        </template>
        <template v-else>
          <div class="bb-client-form-body-item__text">{{ client.skype }}</div>
        </template>
      </div>
      <div class="bb-client-form-body-item"
           v-if="editable || client.id === 'new' || (client.phone && client.phone.length > 0)">
        <label class="bb-form__label bb-form__label_top bb-client-form-body-item__label">Phone</label>
        <template v-if="editable">
          <transition-group tag="div" name="fade" class="bb-client-form-body-email">
            <div class="bb-client-form-body-email__item"
                 v-for="(phone, index) in client.phone"
                 :key="phone + index">
              <input type="text" class="bb-client-form-body-item__input bb-input bb-input_border"
                     autocomplete="off"
                     :class="{'bb-client-form-body-item__input_email': true}"
                     v-model="client.phone[index].number">
              <i class="icon-menu bb-client-form-body-email__menu" @click.stop="openPhoneSetting = index"/>
              <transition name="bounce">
                <div class="bb-client-form-body-item-social"
                     @mouseleave="openPhoneSetting = false"
                     v-if="openPhoneSetting === index"
                     v-click-outside="() => { openPhoneSetting = false}">
                  <div class="bb-client-form-body-item-social__item"
                       v-for="social in socials"
                       :key="social.key + social.title">
                    <checkbox class="bb-proforma-bank-details-checkboxes__checkbox"
                              :checkbox-text="social.title"
                              :name="social.key"
                              :section="index"
                              @checkboxChange="checkboxPhone"
                              :value="client.phone[index][social.key]"/>
                  </div>
                </div>
              </transition>
              <div class="bb-client-form-body-email-add"
                   @click.stop="removePhone(index)"
                   v-if="index !== client.phone.length - 1">
                <i class="icon-minus bb-client-form-body-email-add__minus"/>
              </div>
              <div class="bb-client-form-body-email-add"
                   @click.stop="addPhone()"
                   v-if="index === client.phone.length - 1">
                <i class="icon-cross bb-client-form-body-email-add__icon"/>
              </div>
            </div>
          </transition-group>
        </template>
        <template v-else>
          <div class="bb-client-form-body-item__text">
            <div class="bb-client-form-body-item__phones"
                 v-for="(item, index) in client.phone"
                 :key="item.number + index">
              {{ item.number }}
              <div class="bb-client-form-body-item__social" v-if="item.whatsapp || item.viber || item.telegram">
                <span v-if="item.whatsapp">
                <i class="icon-whatsapp"/>
              </span>
                <span v-if="item.viber" class="bb-client-form-body-item__viber-wrapper">
                <i class="icon-viber-background"/>
                <i class="icon-viber"/>
              </span>
                <span v-if="item.telegram">
                <i class="icon-telegram"/>
              </span>
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="bb-client-form-body-item"
           ref="email"
           v-if="editable || (client.email && client.email.length > 0)">
        <label class="bb-form__label bb-form__label_top bb-client-form-body-item__label">E-mail</label>
        <template v-if="editable">
          <transition-group tag="div" name="fade" class="bb-client-form-body-email">
            <div class="bb-client-form-body-email__item"
                 v-for="(email, index) in client.email"
                 :key="email + index">
              <input type="text" class="bb-client-form-body-item__input bb-input bb-input_border"
                     autocomplete="off"
                     v-model.trim="client.email[index].address"
                     :class="{'bb-input_error' : errors.email[index], 'bb-client-form-body-item__input_email': true}">
              <span v-if="errors.email[index]"
                    class="bb-client-form-body-item_text-error bb-client-form-body-item_text-error_email">
                {{ errors.email[index] }}
              </span>
              <div class="bb-client-form-body-email-add"
                   @click.stop="removeEmail(index)"
                   v-if="index !== client.email.length - 1">
                <i class="icon-minus bb-client-form-body-email-add__minus"/>
              </div>
              <div class="bb-client-form-body-email-add"
                   @click.stop="addEmail()"
                   v-if="index === client.email.length - 1">
                <i class="icon-cross bb-client-form-body-email-add__icon"/>
              </div>
            </div>
          </transition-group>
        </template>
        <template v-else>
          <div class="bb-client-form-body-item__text">
            <div class="bb-client-form-body-item__phones"
                 v-for="(email, index) in client.email"
                 :key="email + index">
              {{ email }}
            </div>
          </div>
        </template>
      </div>
      <div
        v-if="currentContactType === clientId && (editable || client.clientChat)"
        class="bb-client-form-body-item"
      >
        <label class="bb-form__label bb-form__label_top  bb-client-form-body-item__label">Client Chat</label>
        <DynamicInput
          v-if="editable"
          :value.sync="client.clientChat"
          :id="`client-chat_${client.id}`"
        />
        <div
          v-else
          class="bb-client-form-body-item__text"
        >
          {{ client.clientChat }}
        </div>
      </div>
      <div
        v-if="currentContactType === clientId && (editable || client.datePath)"
        class="bb-client-form-body-item"
      >
        <label class="bb-form__label bb-client-form-body-item__label">Data Path</label>
        <DynamicInput
          v-if="editable"
          :value.sync="client.datePath"
          :id="`data-path_${client.id}`"
        />
        <div
          v-else
          class="bb-client-form-body-item__text"
        >
          {{ client.datePath }}
        </div>
      </div>
      <!--      <div class="bb-client-form-body-item"-->
      <!--           v-if="editable || client.id === 'new' || (client.clientType === 2 && client.emailName)">-->
      <!--        <label class="bb-form__label bb-client-form-body-item__label">Name for email</label>-->
      <!--        <template v-if="editable">-->
      <!--          <input type="text" class="bb-client-form-body-item__input bb-input bb-input_border"-->
      <!--                 autocomplete="off"-->
      <!--                 v-model="client.emailName" v-autofocus>-->
      <!--        </template>-->
      <!--        <template v-else>-->
      <!--          <div class="bb-client-form-body-item__text">{{ client.emailName }}</div>-->
      <!--        </template>-->
      <!--      </div>-->
      <div class="bb-client-form-body-item" v-if="editable || client.id === 'new' || client.notes">
        <label class="bb-form__label bb-form__label_top bb-client-form-body-item__label">Note</label>
        <template v-if="editable">
        <textarea class="bb-client-form-body-item__textarea bb-input bb-input_border"
                  v-model="client.notes">
        </textarea>
        </template>
        <template v-else>
          <div class="bb-client-form-body-item__text">
            <pre>{{ client.notes }}</pre>
          </div>
        </template>
      </div>
      <div
        v-if="currentContactType === partnerId && (editable || client.commission)"
        class="bb-client-form-body-item"
      >
        <label class="bb-form__label bb-client-form-body-item__label">Commission</label>
        <ContactFormPercent
          v-if="editable"
          :client="client"
          prop="commission"
        />
        <div v-else>
          {{client.commission}}
        </div>
      </div>
<!--      ContactFormPercent-->
      <div
        v-if="currentContactType === partnerId && (editable || client.referralAgreement.length)"
        class="bb-client-form-body-item bb-client-form-body-item_file"
      >
        <BBUploadFile
          :items.sync="client.referralAgreement"
          :editable="editable"
          :loaderRun.sync="loaderRun"
          label="Referral Agreement"
        />
      </div>
      <template v-if="currentContactType === clientId">
        <div class="bb-client-form-body-residential"
             :class="{'bb-client-form-body-residential_view-mode': !editable}"
             v-if="editable || (client.residentialAddress && client.residentialAddress.length > 0)"
             v-for="(residential, residentialIndex) in client.residentialAddress"
             :key="'key' + residential + residentialIndex">
          <div class="bb-client-form-body-item bb-client-form-body-item_residential" v-if="residential">
            <label class="bb-form__label bb-form__label_top bb-client-form-body-item__label">Residential address</label>
            <template v-if="editable">
              <transition-group tag="div" name="fade" class="bb-client-form-body-email">
                <div class="bb-client-form-body-email__item bb-client-form-body-email__item_residential"
                     :key="'anim' + residential + residentialIndex">
                <textarea class="bb-client-form-body-item__textarea bb-input bb-input_border"
                          autocomplete="off"
                          v-model="client.residentialAddress[residentialIndex].address">
                </textarea>
                  <div class="bb-client-form-body-email-add"
                       @click.stop="removeResidentialAddress(residentialIndex)"
                       v-if="residentialIndex !== client.residentialAddress.length - 1">
                    <i class="icon-minus bb-client-form-body-email-add__minus"/>
                  </div>
                  <div class="bb-client-form-body-email-add"
                       @click.stop="addResidentialAddress()"
                       v-if="residentialIndex === client.residentialAddress.length - 1">
                    <i class="icon-cross bb-client-form-body-email-add__icon"/>
                  </div>
                </div>
              </transition-group>
            </template>
            <template v-else>
              <div class="bb-client-form-body-item__text">
                <div class="bb-client-form-body-item__phones">
                  <pre>{{ residential.address }}</pre>
                </div>
              </div>
            </template>
          </div>
          <div class="bb-client-form-body-item bb-client-form-body-item_residential"
               v-if="residential && (editable || residential.landLord)">
            <label class="bb-form__label bb-form__label_top bb-client-form-body-item__label">Landlord</label>
            <template v-if="editable">
              <div class="bb-client-form-body-email">
                <div class="bb-client-form-body-email__item bb-client-form-body-email__item_residential">
                <textarea class="bb-client-form-body-item__textarea bb-input bb-input_border"
                          autocomplete="off"
                          v-model="client.residentialAddress[residentialIndex].landLord">
                </textarea>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="bb-client-form-body-item__text">
                <div class="bb-client-form-body-item__phones">
                  <pre>{{ residential.landLord }}</pre>
                </div>
              </div>
            </template>
          </div>
          <div class="bb-client-form-body-item bb-client-form-body-item_residential"
               v-if="residential && (editable || residential.leaseExpDate)">
            <label class="bb-form__label bb-client-form-body-item__label">Lease Exp Date</label>
            <template v-if="checkAccessExpDateChange(residential)">
              <DataPicker @setDate="setDate"
                          :long="true"
                          :no-past="residential.expireStatus"
                          :obj="residentialIndex"
                          :default-date="client.residentialAddress[residentialIndex].leaseExpDate"
                          :no-input="clear.leaseExpDate[residentialIndex]"/>
            </template>
            <template v-else>
              <div class="bb-client-form-body-item__text bb-client-form-body-item-date"
                   v-if="client.residentialAddress[residentialIndex].leaseExpDate">
                {{ dateFormat(client.residentialAddress[residentialIndex].leaseExpDate) }}
                <div class="bb-client-form-body-item-date__icon">
                  <i class="icon-calendar"></i>
                </div>
              </div>
            </template>
            <template v-if="(editable && residential.expireStatus === statusUnderRenewal) || client.id === 'new'">
              <i class="icon-cross bb-client-form-body-item-date__clear"
                 @click="clearDate('leaseExpDate', residentialIndex)"/>
            </template>
          </div>
          <div class="bb-client-form-body-item bb-client-form-body-item_residential bb-client-form-body-item_file"
               v-if="residential && (residential.uploadFile || editable)">
            <label class="bb-form__label bb-client-form-body-item__label">Lease Contract</label>
            <div class="bb-client-form-body-item__file"
                 :class="{'bb-client-form-body-item__file_view-mode': !editable}">
              <UploadFileForm filename="contract"
                              :disabled="!editable"
                              :file-id="residential.uploadFile"
                              @loader="setLoader"
                              @remove="deleteFileAddress(residential)"
                              @upload="uploadFileAddress($event, residential)"/>
              <span v-if="errors.residential[residentialIndex]" class="bb-client-form-body-item_text-error
                  bb-client-form-body-item_text-error_address">
                    {{ errors.residential[residentialIndex] }}
                  </span>
            </div>
          </div>
        </div>
        <div class="bb-client-form-body-item" v-if="editable || client.id === 'new' || client.specialClientRequest">
          <label class="bb-form__label bb-form__label_top bb-client-form-body-item__label">
            Special Client’s Requests
          </label>
          <template v-if="editable">
        <textarea class="bb-client-form-body-item__textarea bb-input bb-input_border"
                  v-model="client.specialClientRequest">
        </textarea>
          </template>
          <template v-else>
            <div class="bb-client-form-body-item__text">
              <pre>{{ client.specialClientRequest }}</pre>
            </div>
          </template>
        </div>
        <div class="bb-client-form-body-residential"
             :class="{'bb-client-form-body-residential_view-mode': !editable}"
             v-if="editable || (client.passports && client.passports.length > 0)"
             v-for="(passport, passportIndex) in client.passports"
             :key="'key_passport' + passportIndex">
          <div class="bb-client-form-body-item bb-client-form-body-item_residential"
               v-if="(passport && passport.citizenship) || editable">
            <label class="bb-form__label bb-form__label_top bb-client-form-body-item__label">Citizenship</label>
            <template v-if="editable">
              <div class="bb-client-form-body-item__input bb-client-form-body-item__input_select">
                <v-select label="name"
                          :options="countriesList"
                          :clearable="true"
                          :placeholder="'Citizenship'"
                          :value="getCountry(passport.citizenship)"
                          @search="searchCountries"
                          @input="setCountry($event, passportIndex)">
                  <template #selected-option="{ name, id }">
                    <div class="bb-client-form-body-item-countries bb-client-form-body-item-countries_selected">
                      <CountryFlag :country-id="id"/>
                      <span class="bb-client-form-body-item-countries__name">{{ name }}</span>
                    </div>
                  </template>
                  <template #option="{ name, id }">
                    <div class="bb-client-form-body-item-countries">
                      <CountryFlag :country-id="id"/>
                      <span class="bb-client-form-body-item-countries__name">{{ name }}</span>
                    </div>
                  </template>
                </v-select>
              </div>
              <div class="bb-client-form-body-email-add"
                   @click.stop="removePassports(passportIndex)"
                   v-if="passportIndex !== client.passports.length - 1">
                <i class="icon-minus bb-client-form-body-email-add__minus"/>
              </div>
              <div class="bb-client-form-body-email-add"
                   @click.stop="addPassports()"
                   v-if="passportIndex === client.passports.length - 1">
                <i class="icon-cross bb-client-form-body-email-add__icon"/>
              </div>
            </template>
            <template v-else>
              <div class="bb-client-form-body-item__text bb-client-form-body-item__text_country"
                   v-if="passport.citizenship">
                <CountryFlag :country-id="passport.citizenship" :hide-popup="true"/>
                <div class="bb-client-form-body-item__phones">
                  {{ getCountry(passport.citizenship).name }}
                </div>
              </div>
            </template>
          </div>
          <div class="bb-client-form-body-item bb-client-form-body-item_residential"
               v-if="(passport && passport.number) || editable">
            <label class="bb-form__label bb-form__label_top bb-client-form-body-item__label">Passport No.</label>
            <template v-if="editable">
              <input type="text" class="bb-client-form-body-item__input bb-input bb-input_border"
                     autocomplete="off"
                     v-model="client.passports[passportIndex].number">
            </template>
            <template v-else>
              <div class="bb-client-form-body-item__text">
                <div class="bb-client-form-body-item__phones">
                  {{ passport.number }}
                </div>
              </div>
            </template>
          </div>
          <div class="bb-client-form-body-item bb-client-form-body-item_residential"
               v-if="(passport && passport.date) || editable">
            <label class="bb-form__label bb-form__label_top bb-client-form-body-item__label">Passport Due Date</label>
            <template v-if="editable">
              <DataPicker @setDate="setDatePassport"
                          :long="true"
                          :no-past="true"
                          :obj="passportIndex"
                          :default-date="client.passports[passportIndex].date"
                          :no-input="clear.passportDate[passportIndex]"/>
            </template>
            <template v-else>
              <div class="bb-client-form-body-item__text">
                <div class="bb-client-form-body-item__phones">
                  {{ dateFormat(passport.date) }}
                </div>
              </div>
            </template>
            <template v-if="editable || client.id === 'new'">
              <i class="icon-cross bb-client-form-body-item-date__clear"
                 @click="clearDate('passportDate', passportIndex)"/>
            </template>
          </div>
        </div>
        <div class="bb-client-form-body-residential"
             :class="{'bb-client-form-body-residential_view-mode': !editable}"
             v-if="editable || (client.banks && client.banks.length > 0)"
             v-for="(bank, bankIndex) in client.banks"
             :key="'key_banks' + bankIndex">
          <div class="bb-client-form-body-item bb-client-form-body-item_residential"
               v-if="(bank && (bank.bankName || bank.bankNo)) || editable">
            <label class="bb-form__label bb-form__label_top bb-client-form-body-item__label">Bank</label>
            <template v-if="editable">
              <div class="bb-client-form-body-item__input bb-client-form-body-item__input_select">
                <v-select label="name"
                          :options="banksList"
                          :clearable="true"
                          :placeholder="'Bank'"
                          :value="getBankName(bank.bankName)"
                          @search="searchBanks"
                          @input="setBank($event, bankIndex)">
                  <template #selected-option="{ name }">
                    <div class="bb-client-form-body-item-countries bb-client-form-body-item-countries_bank">
                      <span class="bb-client-form-body-item-countries__name">{{ name }}</span>
                    </div>
                  </template>
                </v-select>
              </div>
              <div class="bb-client-form-body-email-add"
                   @click.stop="removeBank(bankIndex)"
                   v-if="bankIndex !== client.banks.length - 1">
                <i class="icon-minus bb-client-form-body-email-add__minus"/>
              </div>
              <div class="bb-client-form-body-email-add"
                   @click.stop="addBank()"
                   v-if="bankIndex === client.banks.length - 1">
                <i class="icon-cross bb-client-form-body-email-add__icon"/>
              </div>
            </template>
            <template v-else>
              <div class="bb-client-form-body-item__text" v-if="getBankName(bank.bankName)">
                <div class="bb-client-form-body-item__phones">
                  {{ getBankName(bank.bankName).name }}
                </div>
              </div>
            </template>
          </div>
          <div class="bb-client-form-body-item bb-client-form-body-item_residential"
               v-if="(bank && bank.bankNo) || editable">
            <label class="bb-form__label bb-form__label_top bb-client-form-body-item__label">Bank A/C No.</label>
            <template v-if="editable">
              <input type="text" class="bb-client-form-body-item__input bb-input bb-input_border"
                     autocomplete="off"
                     v-model="client.banks[bankIndex].bankNo">
            </template>
            <template v-else>
              <div class="bb-client-form-body-item__text">
                <div class="bb-client-form-body-item__phones">
                  {{ bank.bankNo }}
                </div>
              </div>
            </template>
          </div>
        </div>
        <div class="bb-client-form-body-item" v-if="editable || client.id === 'new' || client.visaExpiration">
          <label class="bb-form__label bb-client-form-body-item__label">Visa Expiry Date</label>
          <template v-if="editable">
            <DataPicker @setDate="setDate"
                        :long="true"
                        obj="visaExpiration"
                        :default-date="client.visaExpiration"
                        :error="errors.visaExpiry"
                        :no-input="clear.visaExpiration"/>
          </template>
          <template v-else>
            <div class="bb-client-form-body-item__text bb-client-form-body-item-date">
              {{ dateFormat(client.visaExpiration) }}
              <div class="bb-client-form-body-item-date__icon">
                <i class="icon-calendar"></i>
              </div>
            </div>
          </template>
          <span v-if="errors.visaExpiry"
                class="bb-client-form-body-item_text-error bb-client-form-body-item_date-error">
          {{ errors.visaExpiry }}
        </span>
          <template v-if="editable || client.id === 'new'">
            <i class="icon-cross bb-client-form-body-item-date__clear" @click="clearDate('visaExpiration')"/>
          </template>
        </div>
        <div class="bb-client-form-body-item" v-if="editable || client.id === 'new' || client.lastFly">
          <label class="bb-form__label bb-client-form-body-item__label">Last Departure</label>
          <template v-if="editable">
            <DataPicker @setDate="setDate"
                        :long="true"
                        obj="lastFly"
                        :default-date="client.lastFly"
                        :no-input="clear.lastFly"/>
          </template>
          <template v-else>
            <div class="bb-client-form-body-item__text bb-client-form-body-item-date" v-if="client.lastFly">
              {{ dateFormat(client.lastFly) }}
              <div class="bb-client-form-body-item-date__icon">
                <i class="icon-calendar"></i>
              </div>
            </div>
          </template>
          <template v-if="editable || client.id === 'new'">
            <i class="icon-cross bb-client-form-body-item-date__clear" @click="clearDate('lastFly')"/>
          </template>
        </div>
        <div class="bb-client-form-body-item" v-if="editable || client.id === 'new' || client.birthday">
          <label class="bb-form__label bb-form__label_no-capitalize bb-client-form-body-item__label">
            Date of Birth
          </label>
          <template v-if="editable">
            <DataPicker @setDate="setDate"
                        :long="true"
                        obj="birthday"
                        :default-date="client.birthday"
                        format="DD-MMM"
                        :no-future="true"
                        :no-input="clear.birthday"/>
          </template>
          <template v-else>
            <div class="bb-client-form-body-item__text bb-client-form-body-item-date">
              {{ dateFormat(client.birthday, 'DD-MMM') }}
              <div class="bb-client-form-body-item-date__icon">
                <i class="icon-calendar"></i>
              </div>
            </div>
          </template>
          <template v-if="editable || client.id === 'new'">
            <i class="icon-cross bb-client-form-body-item-date__clear" @click="clearDate('birthday')"/>
          </template>
        </div>
      </template>
    </VuePerfectScrollbar>
    <div class="bb-client-form-buttons">
      <template v-if="client.id === 'new'">
        <button class="bb-client-form-buttons__add bb-button" @click="setResidentialAddress(true)">ADD</button>
      </template>
      <template v-else>
        <ButtonsEdit v-if="editable"
                     styleBtn="clientForm"
                     :disabled="disableSave"
                     @edit="setResidentialAddress(false)"
                     @delete="closeEdit()"
                     no-pop-up="false"
                     btn-right-text="CANCEL"
                     btn-left-text="SAVE"
                     left-if="true"/>
      </template>
      <DoubleDelete v-if="openDoubleDelete"
                    :id="client.id"
                    :title="client.nickName"
                    :password="passwordClientDelete"
                    @close="openDoubleDelete = false"
                    @delete="deleteContact"/>
    </div>
    <ModalWindow v-if="file" @close="file = false" @click.stop>
      <UploadImage :file="file" @uploadFile="setFileDataForm" @click.stop/>
    </ModalWindow>
    <transition name="fade">
      <ModalWindow @close="showInvoices = false" v-if="showInvoices" :tableMode="true" :title="titleInvoices">
        <ContactInvoices @openProforma="openProforma"/>
      </ModalWindow>
    </transition>
    <transition name="fade">
      <ModalWindow @close="proformaOpen = false, showInvoices = true" v-if="proformaOpen">
        <ProformaInvoice @close="proformaOpen = false"
                         :view-mode="true"
                         :editable-invoice="editableInvoice"/>
      </ModalWindow>
    </transition>
  </div>
</template>

<script>
import CustomSelect from '@/components/Main/Inputs/CustomSelect'
import ContactsApi from '@/api/Contacts/api'
import LoaderFly from '@/components/Main/Loaders/LoaderFly'
import checkbox from '@/components/Main/Inputs/checkbox'
import radio from '@/components/Main/Inputs/radio'
import DataPicker from '@/components/Main/DataPicker/DataPicker'
import UsersListApi from '@/api/UsersList/api'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import ButtonsEdit from '@/components/Main/ButtonsEdit/ButtonsEdit'
import SoaCreditNote from '@/api/SoaCreditNote/api'
import moment from 'moment'
import validateEmail from '@/mixin/validateEmail'
import convertNumber from '@/mixin/convertNumber'
import {find, get, set, cloneDeep, filter, groupBy, sortBy, startCase, uniq, compact, orderBy} from 'lodash'
import {contactStatus, contactType, contactRisk} from '@/constants/clientInfo'
import {contactLanguages} from '@/constants/languages'
import DoubleDelete from '@/components/Main/DoubleDelete/DoubleDelete'
import ModalWindow from '@/components/Main/ModalWindow/ModalWindow'
import UploadImage from '@/components/Main/UploadImage/UploadImage'
import UploadFileApi from '@/api/UploadFile/api'
import ContactsAddressApi from '@/api/ContactsAddress/api'
import avatarUserDefault from '@/assets/images/avatar-user-default-big.png'
import InvoiceApi from '@/api/Invoce/api'
import ProformaInvoice from '@/components/Dissemination/Sales/CRM/ContactCard/ProformaInvoice/ProformaInvoice'
import ContactInvoices from '@/components/Dissemination/Sales/CRM/ContactCard/ContactInvoices/ContactInvoices'
import clientNickname from '@/mixin/clientNickname'
import UploadFileForm from '@/components/Main/UploadFileForm/UploadFileForm'
import {UNDER_RENEWAL} from '@/constants/expireStatus'
import checkFieldUpdate from '@/mixin/checkFieldUpdate'
import {passwordClientDelete} from '@/constants/passwordForDelete'
import countriesList from '@/constants/countriesList'
import vSelect from 'vue-select'
import CountryFlag from '@/components/Main/CountryFlag/CountryFlag'
import personalBanks from '@/constants/personalBanks'
import DynamicInput from '@/components/Main/DynamicInput/DynamicInput'
import ContactFormPartner from '@/components/Dissemination/Sales/CRM/ContactCard/ContactForm/ContactFormPartner'
import ContactFormPercent from '@/components/Dissemination/Sales/CRM/ContactCard/ContactForm/ContactFormPercent'
import { CLIENT_ID, PARTNER_ID } from '@/constants/clientType'
import BBUploadFile from '@/components/Main/BBUploadFile/BBUploadFile'
import setFileData from '@/mixin/setFileData'
import ContactFormPersonality from './ContactFormPersonality'
import {LinkTwoIcon} from '@/constants/icon'

export default {
  name: 'ContactForm',
  props: ['currentContactType'],
  mixins: [validateEmail, convertNumber, clientNickname, checkFieldUpdate, setFileData],
  components: {
    ContactFormPersonality,
    BBUploadFile,
    ContactFormPartner,
    ContactFormPercent,
    DynamicInput,
    CountryFlag,
    UploadFileForm,
    ContactInvoices,
    CustomSelect,
    LoaderFly,
    checkbox,
    radio,
    DataPicker,
    ButtonsEdit,
    DoubleDelete,
    VuePerfectScrollbar,
    ModalWindow,
    UploadImage,
    ProformaInvoice,
    vSelect
  },
  data: function () {
    return {
      clientId: CLIENT_ID,
      partnerId: PARTNER_ID,
      statusUnderRenewal: UNDER_RENEWAL.id,
      passwordClientDelete: passwordClientDelete,
      countResidentialAddress: 0,
      showInvoices: false,
      disableButton: false,
      clientPayments: 0,
      popUpOpen: false,
      socials: [
        {
          key: 'whatsapp',
          title: 'WhatsApp'
        },
        {
          key: 'viber',
          title: 'Viber'
        },
        {
          key: 'telegram',
          title: 'Telegram'
        }
      ],
      editableInvoice: {},
      proformaOpen: false,
      openPhoneSetting: false,
      clear: {
        passportDate: [true],
        visaExpiration: true,
        lastFly: true,
        birthday: true,
        leaseExpDate: []
      },
      openDoubleDelete: false,
      setTimeoutId: -1,
      loaderRun: false,
      openCheckbox: false,
      deleteConfirmOpen: false,
      editable: false,
      fullProjectManager: [],
      totalCreditNotes: 0,
      fullBusinessConsultant: false,
      clientStatus: contactStatus,
      clientRisk: contactRisk,
      clientLanguage: contactLanguages,
      countriesList: countriesList,
      banksList: personalBanks,
      contactType: contactType,
      errors: {
        nickName: false,
        email: [],
        passDate: false,
        visaExpiry: false,
        residential: []
      },
      client: {
        passports: [
          {
            number: '',
            citizenship: '',
            date: ''
          }
        ],
        banks: [
          {
            bankName: 0,
            bankNo: ''
          }
        ],
        projectManager: '',
        id: '',
        clientCounter: false,
        email: [{
          address: ''
        }],
        emailName: '',
        residentialAddress: [{
          address: '',
          leaseExpDate: ''
        }],
        phone: [
          {
            number: '',
            telegram: false,
            whatsapp: false,
            viber: false
          }
        ],
        riskStatus: null,
        languages: [],
        clientChat: '',
        datePath: '',
        partner: '',
        commission: '',
        referralAgreement: [],
        clientPersonalityType: ''
      },
      date: '',
      file: false,
      imgSrc: false,
      disableSave: false,
      pendingFile: false,
      avatarUserDefault: avatarUserDefault,
      autoUpdateClient: false,
      crypt: false,
      linkIcon: LinkTwoIcon(),
      users: []
    }
  },
  computed: {
    pmList () {
      return this.getUsersByDepartments(2)
    },
    bcList () {
      return this.getUsersByDepartments(1)
    },
    bcEntityById () {
      const id = this.client.businessConsultant
      if (id) {
        const user = find(this.users, user => user.id === id)
        return user
      }
      return {}
    },
    pmEntityById () {
      const id = this.client.projectManager
      if (id) {
        const user = find(this.users, user => user.id === id)
        return user
      }
      return {}
    },
    isFounder () {
      // const userInfo = this.$store.getters.userInfo || {}
      // checking permission to edit license without uploading a file
      return true // userInfo.role === FOUNDER.id
    },
    titleInvoices () {
      return 'OVER- AND UNDERPAYMENTS OF ' + this.nicknameClient.toUpperCase()
    },
    contactTypeName () {
      const names = ['', 'Client', 'Partner']
      const type = this.currentContactType || 1
      return names[type]
    },
    activeCompany: function () {
      return this.$store.getters.activeCompany
    },
    /* users: {
      get: function () {
        return this.$store.getters.usersList
      },
      set: function (data) {
        this.$store.dispatch('setUsersList', data)
      }
    }, */
    userInfo () {
      return this.$store.getters.userInfo
    },
    contactsList: {
      get: function () {
        return this.$store.getters.contactsList
      },
      set: function (data) {
        this.$store.dispatch('setContactsList', data)
      }
    },
    clientInfo: {
      get: function () {
        return this.$store.getters.clientInfo
      },
      set: function (data) {
        this.$store.dispatch('setClientInfo', data)
      }
    }
  },
  methods: {
    uploadFileAddress (file, residential) {
      set(residential, 'uploadFile', file.id)
    },
    deleteFileAddress (residential) {
      this.client.residentialAddress = this.client.residentialAddress.map(el => {
        if (el.id === residential.id) {
          const {uploadFile, id, ...newEl} = el
          return newEl
        } else {
          return el
        }
      })
    },
    openProforma (proforma) {
      this.editableInvoice = proforma
      this.proformaOpen = true
    },
    roundNum (num) {
      return Math.round(num * 100) / 100
    },
    getAccountBalance () {
      const balance = this.client.accountBalance
      return balance || 0
    },
    getAccountPayments () {
      const clientID = get(this.clientInfo, 'id', '')
      const query = 'isDelete[]=false&client[]=' + clientID
      InvoiceApi.getInvoicesTotal({query})
        .then((resp) => {
          const overpaid = get(resp.data, 'totalOverpaid', 0)
          const underpaid = get(resp.data, 'totalUnderpaid', 0)
          this.clientPayments = this.roundNum(overpaid - underpaid)
        })
        .catch(e => console.error(e))
    },
    uploadFile (fileData) {
      this.disableSave = true
      this.loaderRun = true
      UploadFileApi.createFile(fileData, this.crypt)
        .then((resp) => {
          this.client.image = resp.data.id
          this.disableSave = false
          this.loaderRun = false
        })
        .catch((err) => {
          this.disableSave = false
          this.loaderRun = false
          console.error(err)
        })
    },
    setFileDataForm (fileData, file) {
      const clientId = this.client.id
      const label = fileData.get('file').name
      fileData.set('fieldname', 'uploadFile')
      fileData.set('label', label)
      fileData.set('parentId', clientId)
      fileData.set('onModel', 'Client')
      this.file = false
      this.imgSrc = file || this.imgSrc
      const cacheImage = new Image()
      cacheImage.src = this.imgSrc
      set(this.client, 'image.path', this.imgSrc)
      this.uploadFile(fileData)
    },
    processFile (e) {
      const files = get(e, 'target.files', [])
      if (!files.length) return
      this.file = files[0]
      this.$emit('setActivePart', 'left')
      e.target.value = ''
    },
    addEmail () {
      const lastIndex = this.client.email.length - 1
      const lastItem = get(this.client.email[lastIndex], 'address', false)
      if (!lastItem && this.client.email[lastIndex]) return
      this.client.email.push({address: ''})
    },
    removeEmail (index) {
      this.client.email.splice(index, 1)
    },
    addPassports () {
      const lastIndex = this.client.passports.length - 1
      const lastItem = get(this.client.passports[lastIndex], 'number', false)
      if (!lastItem && this.client.passports[lastIndex]) return
      this.client.passports.push({
        number: '',
        citizenship: '',
        date: ''
      })
    },
    removePassports (index) {
      this.client.passports.splice(index, 1)
    },
    getBankName (bankId) {
      return find(this.banksList, {id: bankId})
    },
    addBank () {
      const lastIndex = this.client.banks.length - 1
      const lastItem = get(this.client.banks[lastIndex], 'bankName', false)
      if (!lastItem && this.client.banks[lastIndex]) return
      this.client.banks.push({
        bankName: 0,
        bankNo: ''
      })
    },
    removeBank (index) {
      this.client.banks.splice(index, 1)
    },
    addResidentialAddress () {
      const lastIndex = this.client.residentialAddress.length - 1
      const lastItem = get(this.client.residentialAddress[lastIndex], 'address', false)
      if (!lastItem && this.client.residentialAddress[lastIndex]) return
      this.client.residentialAddress.push({address: ''})
    },
    removeResidentialAddress (index) {
      this.client.residentialAddress.splice(index, 1)
    },
    getTotalCreditNote () {
      const { id } = this.$route.params
      if (id === 'new') return
      SoaCreditNote.getTotalCreditNote(id)
        .then(({data}) => {
          const {total} = data
          this.totalCreditNotes = total
        })
        .catch((err) => {
          console.error(err)
        })
    },
    checkboxPhone (data, item, section) {
      set(this.client.phone[section], item, data)
    },
    addPhone () {
      const lastIndex = this.client.phone.length - 1
      const lastItem = get(this.client.phone[lastIndex], 'number', false)
      if (!lastItem && this.client.phone[lastIndex]) return
      this.client.phone.push({
        number: '',
        telegram: false,
        whatsapp: false,
        viber: false
      })
    },
    removePhone (index) {
      this.client.phone.splice(index, 1)
    },
    goToSoa () {
      this.$router.push({name: 'Soa', params: {id: this.clientInfo.id}})
    },
    getToSoaAndOpenTaxCreditNotes () {
      localStorage.setItem('taxCreditNotesOpen', this.contactId)
      this.$router.push({name: 'Soa', params: {id: this.clientInfo.id}})
    },
    scrollTo (to) {
      const scroll = get(this.$refs, 'scrollClientForm.$el', false)
      if (scroll) {
        scroll.scrollTop = to
      }
    },
    checkAccessExpDateChange (residential) {
      const checkStatus = (residential.expireStatus === this.statusUnderRenewal || !residential.expireStatus)
      return this.editable && (checkStatus || this.isFounder)
    },
    dateFormat (date, customFormat) {
      const format = customFormat || 'DD-MMM-YY'
      return moment(new Date(date)).format(format)
    },
    validateForms () {
      let haveError = false
      if (!this.client.nickName) {
        this.errors.nickName = 'mandatory field'
        haveError = true
      } else {
        this.client.firstName = startCase(this.client.firstName)
        this.client.lastName = startCase(this.client.lastName)
        this.errors.nickName = false
      }
      this.client.email.forEach((item, index) => {
        if (item.address && !this.validateEmail(item.address.toLowerCase())) {
          this.$set(this.errors.email, index, 'is invalid')
          haveError = true
        } else {
          this.$set(this.errors.email, index, false)
        }
      })
      return haveError
    },
    errorsCheck (errObj) {
      const errors = get(errObj, 'response.data.errors', '')
      if (!errors) return
      const firstErr = Object.keys(errors)[0]
      if (errors[firstErr] && firstErr === 'email') {
        this.errors[firstErr][0] = errors[firstErr]
      } else if (errors[firstErr]) {
        this.errors[firstErr] = errors[firstErr]
      }
    },
    deleteEmptyProps () {
      this.errors.residential = []
      this.client.email = this.client.email.map(item => {
        item = item.address ? item.address : item
        return typeof item === 'object' ? item.address : item
      })
      this.client.phone = filter(this.client.phone, item => item.number)
      this.client.residentialAddress = filter(this.client.residentialAddress, item => item)
      this.client.email = uniq(compact(this.client.email))
    },
    generateClientCode (client) {
      const contactType = this.currentContactType || 1
      client.contactType = contactType
      const contactCode = find(this.contactType, elem => elem.id === contactType) || {}
      const clientCounter = this.client.clientCounter || 1
      return `BB-${contactCode.code}-${clientCounter}`
    },
    checkedErrors () {
      if (this.errors.email.length > 0) {
        this.scrollTo(430)
      }
      if (this.errors.nickName) {
        this.scrollTo(0)
      }
    },
    setResidentialAddress (isNewClient) {
      if (this.validateForms()) {
        this.checkedErrors()
        return
      }
      if (isNewClient) {
        this.createClient(true)
        return
      }
      const callback = isNewClient ? this.createClient : this.editContact
      const hasResidentialAddress = this.client.residentialAddress && this.client.residentialAddress.length > 0
      if (hasResidentialAddress) {
        const validate = this.client.residentialAddress.map((item, index) => this.checkedUpdateLicenses(item, index))
        if (validate.some(item => !item)) {
          return
        }
        const address = cloneDeep(this.client.residentialAddress)
        const length = address.length
        this.client.residentialAddress = []
        this.countResidentialAddress = 0
        address.forEach(address => {
          this.updateResidentialAddress({address, length, callback})
        })
      } else {
        callback()
      }
    },
    checkedUpdateLicenses (address, index) {
      if (!address) return true
      const oldAddress = find(this.clientInfo.residentialAddress, {id: address.id})
      const options = {newCompany: address, oldCompany: oldAddress, fieldDate: 'leaseExpDate', fieldFile: 'uploadFile'}
      const result = this.checkFieldUpdate(options)
      if (!result) {
        set(this.errors, ['residential', index], 'Need to upload a file')
      }
      return result
    },
    updateResidentialAddress ({address, length, callback}) {
      if (!address) return
      if (!address.address || (!address.address && this.countResidentialAddress === length)) {
        callback()
        return
      }
      const id = address.id
      address.client = this.client._id
      address.user = this.userInfo.id
      delete address.expireStatus
      const request = id ? ContactsAddressApi.updateContactAddress : ContactsAddressApi.createContactAddress
      request(address)
        .then((resp) => {
          this.countResidentialAddress += 1
          const fileId = get(resp.data, 'uploadFile.id', '')
          if (fileId) set(resp.data, 'uploadFile', fileId)
          this.client.residentialAddress.push(resp.data)
          if (this.countResidentialAddress === length) {
            callback()
          }
        })
        .catch((err) => {
          this.countResidentialAddress += 1
          console.error(err)
        })
    },
    async createClient (updateAddress) {
      this.autoUpdateClient = true
      this.deleteEmptyProps()
      this.loaderRun = true
      this.client.clientCode = this.generateClientCode(this.client)
      if (this.pendingFile) delete this.client.image
      const client = await this.generateBody()
      ContactsApi.createContact(client)
        .then((resp) => {
          resp.data.residentialAddress = cloneDeep(this.client.residentialAddress)
          this.editable = false
          this.disableButton = false
          this.clientInfo = cloneDeep(resp.data)
          this.client = cloneDeep(resp.data)
          if (this.clientInfo.isCreated) {
            const creator = this.clientInfo.userDetails[0]
            const systemHistoryObject = {
              id: 1,
              user: {
                firstName: creator.firstName,
                lastName: creator.lastName,
                username: `${creator.firstName} ${creator.lastName}`,
                image: this.clientInfo.userImage.length > 0
                  ? { path: this.clientInfo.userImage[0].path }
                  : null
              },
              eventDate: this.clientInfo.createdAt,
              eventDescription: this.clientInfo.isCreated,
              height: 13,
              isSystem: true
            }
            this.$store.dispatch('setCardCreatedHistoryEntity', systemHistoryObject)
          }
          this.contactsList.clients.unshift(this.clientInfo)
          this.$router.push({name: 'ContactCard', params: this.clientInfo})
          this.client.clientCounter = resp.data.clientCounter
          this.client.projectManager = this.fullProjectManager
          this.client.businessConsultant = this.fullBusinessConsultant
          this.contactsList.clients[0].projectManager = this.fullProjectManager
          this.contactsList.clients[0].businessConsultant = this.fullBusinessConsultant
          this.contactsList.clients[0].residentialAddress = this.client.residentialAddress
          this.loaderRun = false
          this.scrollTo(0)
          if (updateAddress) this.setResidentialAddress()
        })
        .catch((err) => {
          this.autoUpdateClient = false
          this.loaderRun = false
          this.errorsCheck(err)
          console.error(err)
        })
    },
    checkCompany () {
      if (this.activeCompany !== this.clientInfo.company && this.clientInfo.company) {
        this.$router.push({name: 'ContactsList'})
      }
    },
    getClient () {
      if (this.$route.params.id === 'new') {
        return
      }
      ContactsApi.getContact(this.$route.params.id)
        .then((resp) => {
          this.checkCompany()
          this.clientInfo = cloneDeep(resp.data)
          this.client = cloneDeep(resp.data)
          this.getAccountPayments()
        })
        .catch((err) => {
          console.error(err)
        })
    },
    editForm () {
      if (this.disableButton) return
      const emails = get(this.client, 'email', [])
      emails.length ? this.setEmail() : this.addEmail()
      const phones = get(this.client, 'phone', [])
      if (!phones.length) this.addPhone()
      const residentialAddress = get(this.client, 'residentialAddress', [])
      if (!residentialAddress.length) this.addResidentialAddress()
      const passports = get(this.client, 'passports', [])
      if (!passports.length) this.addPassports()
      const banks = get(this.client, 'banks', [])
      if (!banks.length) this.addBank()
      this.editable = true
      this.disableButton = true
    },
    setStatus (item) {
      this.client.status = item.id
    },
    setLanguage (item) {
      this.client.languages = item.id
    },
    setRisk (item) {
      this.client.riskStatus = item.id
    },
    setCountry (item, passIndex) {
      set(this.client, ['passports', passIndex, 'citizenship'], item ? item.id : '')
    },
    setBank (item, bankIndex) {
      set(this.client, ['banks', bankIndex, 'bankName'], item ? item.id : '')
    },
    searchCountries (word) {
      if (!word) {
        this.countriesList = orderBy(this.countriesList, ['name'], ['asc'])
      } else {
        const reg = new RegExp('^' + word, 'gmi')
        this.countriesList = this.countriesList.sort((a, b) => {
          return reg.test(b.name) - reg.test(a.name)
        })
      }
    },
    searchBanks (word) {
      if (!word) {
        this.banksList = orderBy(this.banksList, ['name'], ['asc'])
      } else {
        const reg = new RegExp('^' + word, 'gmi')
        this.banksList = this.banksList.sort((a, b) => {
          return reg.test(b.name) - reg.test(a.name)
        })
      }
    },
    getCountry (countryId) {
      return find(this.countriesList, {id: countryId})
    },
    getStatusName (id) {
      const status = find(this.clientStatus, elem => elem.id === +id)
      if (status) {
        return status.name
      }
      return status
    },
    getRiskTitle (id) {
      const risk = find(this.clientRisk, elem => elem.id === id)
      return risk ? risk.name : ''
    },
    getLanguageName (id) {
      const language = find(this.clientLanguage, elem => elem.id === +id) || {}
      return language.name
    },
    setConsultant (item) {
      if (!item) return
      this.fullBusinessConsultant = item
      this.client.businessConsultant = item.id
    },
    setProjectManager (item) {
      if (!item) return
      this.fullProjectManager = item.id === 0 ? null : item
      this.client.projectManager = item.id
    },
    setDatePassport (date, obj, def) {
      let clearStatus = true
      if (def.defaultDate || !def.noDefaultDate) {
        clearStatus = false
      }
      set(this.clear.passportDate, obj, clearStatus)
      this.$set(this.client.passports[obj], 'date', clearStatus ? '' : date)
    },
    setDate (date, obj, def) {
      let clearStatus = true
      if (def.defaultDate || !def.noDefaultDate) {
        clearStatus = false
      }
      const leaseExpDate = typeof obj === 'number'
      if (leaseExpDate) {
        set(this.clear.leaseExpDate, obj, clearStatus)
        this.$set(this.client.residentialAddress[obj], 'leaseExpDate', date)
      } else {
        set(this.clear, obj, clearStatus)
        set(this.client, obj, date)
      }
      if (clearStatus && leaseExpDate) {
        this.clearDate('leaseExpDate', obj)
      } else if (clearStatus && !leaseExpDate) {
        this.clearDate(obj)
      }
    },
    clearDate (type, index) {
      if (type === 'passportDate') {
        set(this.clear.passportDate, index, true)
        this.$set(this.client.passports[index], 'date', '')
      } else if (type === 'leaseExpDate') {
        set(this.clear.leaseExpDate, index, true)
        this.$set(this.client.residentialAddress[index], 'leaseExpDate', '')
      } else {
        set(this.clear, type, true)
        set(this.client, type, '')
      }
    },
    checkboxChange (data, item, section) {
      if (section === 'projectManager') {
        if (data) {
          this.fullProjectManager.push(item)
          this.client.projectManager.push(item.id)
        } else {
          const indexFull = this.fullProjectManager.indexOf(item)
          this.fullProjectManager.splice(indexFull, 1)
          const index = this.client.projectManager.indexOf(item.id)
          this.client.projectManager.splice(index, 1)
        }
      } else {
        this.client[item] = data
      }
    },
    hidePopUp () {
      this.popUpOpen = false
    },
    setNewProforma (data) {
      this.clientInfo.invoiceType = +data
      this.$emit('openProforma', true)
      this.hidePopUp()
    },
    scrollHeader (e) {
      const scroll = get(this.$refs, 'scrollClientForm.$el', false)
      if (scroll) {
        scroll.scrollTop += e.deltaY
      }
    },
    radioChange (data) {
      if (+data === 1) {
        this.$set(this.client, 'clientType', 1)
      } else {
        this.$set(this.client, 'clientType', 2)
      }
    },
    getUsersByDepartments (type) {
      const users = filter(this.users, user => {
        const position = user.position || {}
        const department = position.department
        return department === type
      })
      const groupUsers = groupBy(users, 'isNotAssigned')
      const isNotAssigned = groupUsers.true || []
      const sortedUsers = orderBy(groupUsers.false, [item => item.firstName.toLowerCase()], ['asc'])
      return [...isNotAssigned, ...sortedUsers]
    },
    getUserList () {
      UsersListApi.getUsersList({limit: 1000, assigned: true})
        .then((resp) => {
          const users = filter(resp.data.users, (item) => {
            const department = get(item, 'position.department', false)
            if (department === 1 || department === 2) return item
          })
          const groupUsers = groupBy(users, 'isNotAssigned')
          const customUsers = sortBy(groupUsers.false, ['username'])
          const systemUsers = sortBy(groupUsers.true, ['username'])
          this.users = [...systemUsers, ...customUsers]
        })
        .catch((err) => {
          console.error(err)
        })
    },
    closeEdit () {
      this.deleteEmptyProps()
      this.editable = false
      this.disableButton = false
      this.scrollTo(0)
      this.getClient()
    },
    async uploadFiles (fileData) {
      if (fileData.id) return fileData
      const file = this.setFileData(fileData)
      try {
        const { data } = await UploadFileApi.createFile(file, true)
        return data
      } catch (err) {
        console.error(err)
      }
    },
    async files ({referralAgreement = []}) {
      const promises = referralAgreement.map(async item => {
        item = await this.uploadFiles(item)
        return item
      })
      const result = await Promise.all(promises)
      return result.map((el) => el.id)
    },
    async generateBody () {
      const client = cloneDeep(this.client)
      client.email = [...new Set(client.email)]
      client.referralAgreement = await this.files(client)
      client.residentialAddress = client.residentialAddress.map(item => item.id)
      client.firstName = client.firstName ? client.firstName.trim() : ''
      client.lastName = client.lastName ? client.lastName.trim() : ''
      client.nickName = client.nickName ? client.nickName.trim() : ''
      const partnerId = get(client, 'partner.id', undefined)
      client.partner = partnerId === 'not assigned' ? null : partnerId
      return client
    },
    async editContact (noClose) {
      this.loaderRun = true
      this.deleteEmptyProps()
      const idImage = get(this.client, 'image.id', false)
      const oldImg = this.client.image
      if (idImage) set(this.client, 'image', idImage)
      const idBusinessConsultant = get(this.client, 'businessConsultant.id', false)
      if (idBusinessConsultant) set(this.client, 'businessConsultant', idBusinessConsultant)
      const idProjectManager = get(this.client, 'projectManager.id', false)
      if (idProjectManager) set(this.client, 'projectManager', idProjectManager)
      const client = await this.generateBody()
      ContactsApi.editContact(client, this.autoUpdateClient)
        .then((resp) => {
          this.loaderRun = false
          this.autoUpdateClient = false
          this.disableSave = false
          resp.data.image = oldImg
          this.client = resp.data
          this.clientInfo = resp.data
          this.contactsList.clients.forEach((item, index) => {
            if (item.id === resp.data.id) {
              this.contactsList.clients.splice(index, 1, resp.data)
              this.contactsList.clients[index].businessConsultant = this.fullBusinessConsultant
              this.contactsList.clients[index].projectManager = this.fullProjectManager
              this.contactsList.clients[index].residentialAddress = this.client.residentialAddress
            }
          })
          if (this.imgSrc) {
            const imageId = get(resp, 'data.image.id', '')
            const img = {
              path: this.imgSrc,
              id: imageId
            }
            set(this.client, 'image', img)
          }
          if (noClose) return
          this.editable = false
          this.disableButton = false
          this.scrollTo(0)
        })
        .catch((err) => {
          this.loaderRun = false
          this.autoUpdateClient = false
          this.disableSave = false
          this.errorsCheck(err)
          console.error(err)
        })
    },
    getContactsList () {
      ContactsApi.getContactsList({})
        .then((resp) => {
          resp.data.clients = filter(resp.data.clients, item => item)
          this.contactsList = resp.data
        })
        .catch((err) => {
          console.error(err)
        })
    },
    deleteContact (id) {
      this.contactsList.clients.forEach((item, index) => {
        if (item.id === id) {
          this.contactsList.clients.splice(index, 1)
        }
      })
      ContactsApi.deleteContact(id)
        .then((resp) => {
          this.$router.push({name: 'ContactsList'})
        })
        .catch((err) => {
          console.error(err)
        })
    },
    setEmail () {
      const emails = []
      this.client.email.forEach(item => {
        emails.push({address: item})
      })
      this.client.email = emails
    },
    setLoader (value) {
      this.loaderRun = value
    }
  },
  watch: {
    activeCompany () {
      this.checkCompany()
    }
  },
  beforeMount () {
    if (this.$route.params.id === 'new') {
      this.clientInfo = {}
      this.client.id = 'new'
      this.editable = true
    }
    if (this.clientInfo.id && this.$route.params.id !== 'new') {
      this.client = cloneDeep(this.clientInfo)
    }
    if (!this.contactsList.clients || this.contactsList.clients.length === 0) {
      this.getContactsList()
    }
    this.getUserList()
    this.getClient()
    this.getTotalCreditNote()
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../../../sass/variables";
@import "../../../../../../sass/mixins";

.bb-client-form {
  @include global-font;
  position: relative;
  display: grid;
  grid-template-rows: 55px auto 1fr auto;
  width: 446px;
  height: calc(100vh - 60px);
  background: $color-white;
  border-radius: 5px;
  font-size: 12px;
  z-index: 1;

  &-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid $color-wild-sand;
    min-height: 55px;
    padding: 0 20px;

    &__title {
      color: $color-accent;
      font-weight: 600;
      font-size: 15px;
      line-height: 18px;
    }

    &-toolbar {
      display: grid;
      grid-template-columns: 25px 25px;
      grid-gap: 6px;
    }
  }

  &-header {
    display: grid;
    grid-template-columns: 140px 1fr;
    margin: 25px 20px 14px 25px;

    &-avatar-wrapper {
      position: relative;
      display: flex;
      justify-content: center;
      margin-right: 29px;
      height: 111px;

      &__avatar {
        width: 111px;
        height: 111px;
        border-radius: 50%;
      }

      &__input,
      &__edit {
        position: absolute;
        bottom: -6px;
        width: 30px;
        height: 30px;
        font-size: 0;
        cursor: pointer;
      }

      &__vip {
        color: $color-accent;
        font-size: 20px;
        width: 20px;
        height: 20px;
        margin-left: 2px;
      }

      &__input {
        opacity: 0;
      }
    }

    &-user-info {
      width: 100%;

      &-totals {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        margin-right: -10px;
      }

      &__fullname {
        display: flex;
        align-items: center;
        color: $color-black;
        font-weight: 500;
        font-size: 16px;
        line-height: 19px;
        margin-bottom: 12px;
      }

      &__code {
        position: absolute;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $color-gray;
        font-weight: 600;
        font-size: 11px;
        line-height: 13px;
        padding: 6px 11px;
        width: 100%;
        background-color: $color-white;
      }

      &__balance {
        flex-grow: 1;
        white-space: nowrap;
        padding: 10px 12px;
        background: rgba($color-silver-chalice, 0.2);
        border-radius: 7px;
        color: $color-gray;
        font-size: 13px;
        line-height: 16px;
        font-weight: 600;
        margin-right: 10px;
        cursor: pointer;
        margin-bottom: 10px;

        &_negative {
          color: $color-pomegranate;
          background-color: $color-cinderella;
        }

        &_positive {
          color: $color-japanese-laurel;
          background-color: rgba($color-japanese-laurel, 0.2);
        }

        i {
          margin-right: 4px;
        }

        &:hover {
          opacity: 0.8;
        }
      }
    }

    &-new-proforma {
      position: relative;
      display: flex;
      align-items: center;

      &__button {
        width: 100%;
        height: 36px;
        font-weight: 600;
        font-size: 11px;
        color: $color-white;
      }
    }
  }

  &-body {
    position: relative;
    height: 100%;
    max-height: calc(100vh - 349px);
    overflow: hidden;

    &_view-mode {
      max-height: calc(100vh - 287px);
    }

    &-email {
      display: flex;
      justify-content: center;
      flex-direction: column;
      flex-grow: 1;

      &-social {
        display: flex;
        align-items: center;
        justify-content: center;
        max-height: 32px;
        margin-left: 8px;
        cursor: pointer;
        color: $color-dusty-gray;

        i:before {
          margin: 0;
        }
      }

      &__item {
        position: relative;
        display: grid;
        grid-template-columns: auto 38px;
        align-items: center;
        margin-bottom: 18px;
        width: 100%;

        &:last-child {
          margin-bottom: 0;
        }

        &_residential {
          align-items: flex-start;
        }

        &_link {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background-color: $color-wild-sand;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          color: $color-denim;
        }
      }

      &__menu {
        padding: 10px;
        position: absolute;
        transform: rotate(90deg);
        color: $color-accent;
        right: 41px;
        cursor: pointer;

        &:hover {
          color: $color-accent-hover;
        }
      }

      &-add {
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid $color-wild-sand;
        box-sizing: border-box;
        border-radius: 5px;
        min-width: 30px;
        width: 30px;
        height: 30px;
        margin-left: 8px;
        cursor: pointer;
        flex-basis: 30px;

        &:hover &__icon {
          color: $color-accent-hover;
        }

        &:hover &__minus {
          color: $color-accent-hover;
        }

        &__icon {
          color: $color-accent;
          transform: rotate(45deg);
        }

        &__minus {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2px;
          color: $color-accent;
        }
      }
    }

    &-residential {
      border: 1px dashed $color-seashell;
      box-sizing: border-box;
      border-radius: 5px;
      margin: 9px 25px 9px 15px;
      transition: 0.3s;

      &_view-mode {
        border-color: transparent;
        margin: 0;
      }
    }

    &-item {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: 30px;
      font-size: 12px;
      line-height: 14px;
      margin-bottom: 18px;
      padding-right: 20px;
      padding-left: 25px;

      &__file {
        margin-right: auto;
        margin-left: 0;
        transition: 0.3s;

        &_view-mode {
          margin-right: 0;
          margin-left: auto;
        }
      }

      &_residential {
        position: relative;
        padding: 10px;
        margin: 0;
      }

      &_file {
        align-items: center;
      }

      &__label {
        font-weight: 600;
        font-size: 13px;
        line-height: 16px;
        min-width: 180px;
      }

      &-social {
        position: absolute;
        top: 25px;
        right: 47px;
        display: inline-flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 12px;
        background: $color-white;
        box-shadow: 0 0 20px rgba($color-black, 0.05);
        border-radius: 3px;
        z-index: 2;

        &__item {
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          user-select: none;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }

      &_select {
        height: 30px;
      }

      &_textarea {
        height: 82px;
        align-items: flex-start;

        &_disable {
          height: 82px;
        }
      }

      &__input {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
        font-size: 12px;

        &__text {
          margin-left: 6px;
        }

        &_select {
          height: 32px;
        }

        &_email {
          height: 32px;
          max-height: 32px;
          flex-basis: 89%;
        }

        &_capitalize {
          text-transform: capitalize;
        }
      }

      &_text-error {
        position: absolute;
        right: 32px;
        color: $color-red-orange;
        font-size: 11px;
        line-height: 13px;

        &_address {
          bottom: 20px;
          right: 10px;
        }

        &_email {
          right: 45px;
        }
      }

      &__text {
        padding: 9px 11px 9px 16px;

        &_country {
          display: grid;
          grid-template-columns: 22px auto;
          grid-gap: 5px;
          align-items: center;
        }
      }

      &__phones {
        display: flex;
        align-items: center;
        min-height: 20px;
      }

      &__social {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-left: 10px;
        color: $color-silver-chalice
      }

      &__viber-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
      }

      .icon-telegram {
        color: $color-accent-hover;
        font-size: 12px;
      }

      .icon-whatsapp {
        color: $color-mountain-meadow;
        font-size: 14px;
      }

      .icon-viber {
        position: relative;
        color: $color-white;
        font-size: 18px;
        z-index: 1;
      }

      .icon-viber-background {
        font-size: 16px;
        position: absolute;
        color: $color-wisteria;
      }

      &-date {
        display: flex;
        align-items: center;

        &__clear {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          min-width: 30px;
          min-height: 30px;
          border-radius: 5px;
          border: 1px solid $color-wild-sand;
          color: $color-accent;
          margin-left: 10px;
          cursor: pointer;

          &:hover {
            color: $color-accent-hover;
          }
        }

        &__icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          margin-left: 5px;
          color: $color-accent;
        }
      }

      &__textarea {
        resize: none;
        width: 100%;
        font-size: 12px;
        height: 82px;
      }

      &-countries {
        display: grid;
        grid-template-columns: 22px auto;
        grid-gap: 5px;
        align-items: center;

        &_selected {
          height: 32px;
        }

        &_bank {
          line-height: 28px;
          grid-template-columns: auto;
        }
      }
    }

    &-checkboxes {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: start;
      transition: all .15s linear;
      line-height: 14px;
      text-transform: capitalize;
      min-height: 32px;
      background-color: $color-white;

      &_open {
        max-height: 120px;
        height: auto;
        min-height: 32px;
        z-index: 1;
      }

      &__checkboxMain {
        position: relative;
        display: flex;
        align-items: center;
        z-index: 1;
      }

      &__checkboxList {
        margin: 8px auto 0 auto;

        &:first-child {
          margin-top: 10px;
        }
      }

      &__arrow {
        position: absolute;
        right: 12px;
        font-size: 5px;
        color: $color-accent;
        transition: all .2s linear;
        cursor: pointer;
      }
    }

    &_view-mode {
      margin-top: 7px;

      .bb-client-form-body {
        &-item {
          align-items: flex-start;
          height: auto;
          min-height: auto;
          margin: 0 20px;
          padding: 13px 0;
          border-bottom: 1px solid $color-wild-sand;

          &_file {
            align-items: center;
          }

          &__text {
            font-weight: normal;
            font-size: 13px;
            line-height: 16px;
            padding: 0;

            pre {
              white-space: pre-wrap;
            }
          }

          &-date {
            height: 16px;
            margin-right: -12px;
          }

          &__label {
            margin: 0;
            padding: 0;
          }

          &__input {
            justify-content: flex-end;
          }
        }
      }
    }
  }

  &-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 25px;
    margin-bottom: 25px;
    width: 100%;

    &__add {
      width: 90px;
      box-sizing: border-box;
      border: 2px solid transparent;
      font-size: 13px;
      line-height: 16px;
    }
  }

  .bb-table-body-item-link {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: $color-wild-sand;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    color: $color-denim;

    &_empty {
      cursor: initial;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Dissemination/Sales/CRM/ContactCard/ContactForm/ContactForm.vue